// @ts-nocheck
import { useEffect, useState } from "react";
import { Box, Checkbox, Grid, InputLabel, Link, Typography } from "@mui/material";
import { CustomInput } from "../Custom/Input";
import { getCssVariableHandler } from "../../utils/styles/getCssVariables";
import {
  fetchServiceAgreementBlobUrl,
  fetchServiceAgreementPreviewBlobUrl,
  getServiceAgreementDocument,
  serviceAgreementPdfUrl,
  type ServiceAgreementDocument,
} from "../../api/clientAgreements";

/**
 * "I agree" block of the ShowPhaze Service Agreement (finance request
 * 2026-09-16, item 10). Used by the public client signup and by the
 * in-app acceptance for existing clients. The PDF shown is the exact
 * document the backend keeps as the current version; the typed full name
 * is the electronic signature (no drawing, no third-party e-sign).
 *
 * The backend validates the same three fields (`client-agreements.rules.ts`);
 * the Yup rules below only mirror them for the form UX.
 */
export interface ServiceAgreementFormValue {
  agree: boolean;
  signerName: string;
  signerTitle: string;
}

export const emptyServiceAgreementValue = (): ServiceAgreementFormValue => ({
  agree: false,
  signerName: "",
  signerTitle: "",
});

export const SERVICE_AGREEMENT_FORM_ERRORS = {
  agree: 'You must tick "I agree" to accept the Service Agreement',
  signerName: "Please type your full name (2 to 120 characters)",
  signerTitle: "Title must be at most 80 characters",
};

/** Yup shape for `serviceAgreement` — same limits as the backend. */
export const serviceAgreementYupShape = (Yup) =>
  Yup.object().shape({
    agree: Yup.boolean().oneOf([true], SERVICE_AGREEMENT_FORM_ERRORS.agree),
    signerName: Yup.string()
      .trim()
      .min(2, SERVICE_AGREEMENT_FORM_ERRORS.signerName)
      .max(120, SERVICE_AGREEMENT_FORM_ERRORS.signerName)
      .required(SERVICE_AGREEMENT_FORM_ERRORS.signerName),
    signerTitle: Yup.string().trim().max(80, SERVICE_AGREEMENT_FORM_ERRORS.signerTitle),
  });

interface Props {
  value: ServiceAgreementFormValue;
  onChange: (next: ServiceAgreementFormValue) => void;
  /** Formik-style errors / touched for the three fields (optional). */
  errors?: Partial<Record<keyof ServiceAgreementFormValue, string>>;
  touched?: Partial<Record<keyof ServiceAgreementFormValue, boolean>>;
  onBlur?: (field: keyof ServiceAgreementFormValue) => void;
  disabled?: boolean;
  /** Company name shown in the agree sentence when known. */
  companyName?: string;
  /** Height of the embedded PDF viewer. */
  viewerHeight?: number | string;
}

const labelSx = {
  color: "#fff",
  fontSize: "14px",
  fontWeight: 500,
  marginBottom: "4px",
  "&.Mui-focused": { color: "#fff" },
};

const errorSx = { color: "#ff6b6b", fontSize: "12px", display: "block", marginTop: "4px" };

export const ServiceAgreementAcceptance = ({
  value,
  onChange,
  errors = {},
  touched = {},
  onBlur,
  disabled = false,
  companyName,
  viewerHeight = 440,
}: Props) => {
  const [doc, setDoc] = useState<ServiceAgreementDocument | null>(null);
  const [frameUrl, setFrameUrl] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getServiceAgreementDocument().then(({ response }) => {
      if (alive) setDoc(response?.data?.data ?? response?.data ?? null);
    });
    return () => {
      alive = false;
    };
  }, []);

  const [frameHash, setFrameHash] = useState("#toolbar=1&navpanes=0");
  const [previewing, setPreviewing] = useState(false);

  // The viewer needs a same-origin blob URL (the API refuses to be framed cross-origin).
  useEffect(() => {
    let alive = true;
    let objectUrl: string | null = null;
    fetchServiceAgreementBlobUrl(doc?.version).then((url) => {
      if (!alive) {
        if (url) URL.revokeObjectURL(url);
        return;
      }
      objectUrl = url;
      // No blob (API down, blocked fetch): try the direct URL; the "open in a new tab" link stays as the way out.
      setFrameUrl((current) => current ?? url ?? serviceAgreementPdfUrl(doc?.version));
    });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc?.version]);

  // Live preview: once the person types, the pdf-service stamps CLIENT NAME / the
  // signature block with what was typed (watermarked PREVIEW) and the viewer jumps
  // to the signature page. Debounced so the PDF is not rebuilt on every keystroke.
  const typedName = value.signerName.trim();
  const typedTitle = value.signerTitle.trim();
  const typedCompany = (companyName || "").trim();
  useEffect(() => {
    if (!typedName && !typedTitle && !typedCompany) return undefined;
    const controller = new AbortController();
    let objectUrl: string | null = null;
    const timer = setTimeout(async () => {
      setPreviewing(true);
      const preview = await fetchServiceAgreementPreviewBlobUrl(
        { version: doc?.version, companyName: typedCompany, signerName: typedName, signerTitle: typedTitle },
        controller.signal,
      );
      setPreviewing(false);
      if (!preview || controller.signal.aborted) {
        if (preview) URL.revokeObjectURL(preview.url);
        return;
      }
      objectUrl = preview.url;
      setFrameHash(`#toolbar=1&navpanes=0${preview.signaturePage && typedName ? `&page=${preview.signaturePage}` : ""}`);
      setFrameUrl(preview.url);
    }, 700);
    return () => {
      clearTimeout(timer);
      controller.abort();
      if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl as string), 5000);
    };
  }, [typedName, typedTitle, typedCompany, doc?.version]);

  const pdfUrl = serviceAgreementPdfUrl(doc?.version);
  const set = (patch: Partial<ServiceAgreementFormValue>) => onChange({ ...value, ...patch });
  const show = (field: keyof ServiceAgreementFormValue) => touched?.[field] && errors?.[field];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <Box>
        <Typography sx={{ color: "#00EEC5", fontWeight: 700, fontSize: "18px" }}>
          {doc?.title || "ShowPhaze Service Agreement"}
          {doc?.version ? (
            <Typography component="span" sx={{ color: "#bbb", fontSize: "13px", marginLeft: "8px" }}>
              version {doc.version}
            </Typography>
          ) : null}
        </Typography>
        <Typography sx={{ color: "#ddd", fontSize: "14px", lineHeight: "22px" }}>
          Please read the agreement below. Every ShowPhaze show is booked under these terms, so it must be accepted
          once per company by an authorized representative.{" "}
          <Link href={pdfUrl} target="_blank" rel="noreferrer" sx={{ color: "#D175B6" }}>
            Open the PDF in a new tab
          </Link>
          .
        </Typography>
      </Box>

      <Box
        sx={{
          border: "2px solid #D175B6",
          borderRadius: "12px",
          overflow: "hidden",
          background: "#fff",
          height: viewerHeight,
        }}
      >
        {frameUrl ? (
          <iframe
            key={`${frameUrl}${frameHash}`}
            title="ShowPhaze Service Agreement"
            src={`${frameUrl}${frameHash}`}
            style={{ width: "100%", height: "100%", border: 0, opacity: previewing ? 0.6 : 1, transition: "opacity 200ms" }}
          />
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#555" }}>
            Loading the agreement…
          </Box>
        )}
      </Box>

      <Typography sx={{ color: "#bbb", fontSize: "12px", marginTop: "-6px" }}>
        As you type below, the agreement above updates with your company name and your signature (marked PREVIEW until you sign).
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <InputLabel sx={labelSx}>Full name (this is your electronic signature)</InputLabel>
          <CustomInput
            name="serviceAgreement.signerName"
            value={value.signerName}
            onChange={(e) => set({ signerName: e.target.value })}
            onBlur={() => onBlur?.("signerName")}
            fullWidth
            disabled={disabled}
            placeholder="Type your full legal name"
            inputProps={{ maxLength: 120, "aria-label": "Full name" }}
          />
          {show("signerName") ? <span style={errorSx}>{errors.signerName}</span> : null}
        </Grid>
        <Grid item xs={12} md={5}>
          <InputLabel sx={labelSx}>Title (optional)</InputLabel>
          <CustomInput
            name="serviceAgreement.signerTitle"
            value={value.signerTitle}
            onChange={(e) => set({ signerTitle: e.target.value })}
            onBlur={() => onBlur?.("signerTitle")}
            fullWidth
            disabled={disabled}
            placeholder="e.g. Events Manager"
            inputProps={{ maxLength: 80, "aria-label": "Title" }}
          />
          {show("signerTitle") ? <span style={errorSx}>{errors.signerTitle}</span> : null}
        </Grid>
      </Grid>

      <Box>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
          <Checkbox
            name="serviceAgreement.agree"
            checked={!!value.agree}
            disabled={disabled}
            onChange={(e) => {
              set({ agree: e.target.checked });
              onBlur?.("agree");
            }}
            sx={{ color: getCssVariableHandler({ variableName: "--font-rgb" }), padding: 0 }}
          />
          <Typography sx={{ color: "#fff", fontSize: "14px", lineHeight: "22px" }}>
            I have read the ShowPhaze Service Agreement and I agree to its terms
            {companyName ? (
              <>
                {" "}
                on behalf of <strong>{companyName}</strong>
              </>
            ) : (
              " on behalf of my company"
            )}
            . By ticking this box and typing my name above I am signing this agreement electronically, and I
            confirm I am authorized to do so.
          </Typography>
        </Box>
        {show("agree") ? <span style={errorSx}>{errors.agree}</span> : null}
      </Box>
    </Box>
  );
};

export default ServiceAgreementAcceptance;
